import type { ComponentPropsWithRef, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn.ts';
import { PillLabel } from '../pill-label/pill-label.tsx';

/* Prototype pills are normal case (700 weight + tracking-wide) — never uppercase. */
const typePillVariants = cva(
  'inline-flex items-center rounded-full text-2xs font-bold tracking-wide whitespace-nowrap shrink-0',
  {
    variants: {
      type: {
        oneOnOne: 'bg-type-oneonone-tint text-type-oneonone',
        functionering: 'bg-type-func-tint text-type-func',
        disfunctionering: 'bg-type-dysfunc-tint text-type-dysfunc',
        reintegratie: 'bg-type-reint-tint text-type-reint',
        selectie: 'bg-type-select-tint text-type-select',
        none: 'bg-type-none-tint text-type-none',
        /* A journey is not a gesprekstype, so it borrows none of their colours. */
        journey: 'bg-pink-tint text-on-pink',
      },
      size: {
        md: 'px-2.5 py-[3px]',
        sm: 'px-2 py-0.5',
      },
      /* For a pill that is a link: the consumer's router element wears these classes. */
      interactive: {
        true: 'cursor-pointer transition-opacity hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current',
        false: '',
      },
    },
    defaultVariants: {
      size: 'md',
      interactive: false,
    },
  },
);

type TypePillVariants = VariantProps<typeof typePillVariants>;

/* Padding either side of the text, in rem, per size. */
const chromeRem = { md: 1.25, sm: 1 } as const;

/*
 * With `short`, the label is a string and the pill trades it for `short` when
 * the card's pill slot has no room for it; see `PillLabel`.
 */
type TypePillLabel =
  | { short?: undefined; children?: ReactNode }
  | { short: string; children: string };

export type TypePillProps = Omit<ComponentPropsWithRef<'span'>, 'style' | 'children'> &
  Omit<TypePillVariants, 'type'> &
  TypePillLabel & {
    type: NonNullable<TypePillVariants['type']>;
  };

export function TypePill({
  className,
  type,
  size,
  short,
  children,
  ...props
}: TypePillProps) {
  return (
    <span
      data-slot='type-pill'
      className={cn(
        typePillVariants({ type, size }),
        short !== undefined && 'min-w-0 shrink',
        className,
      )}
      {...props}
    >
      {short === undefined ? (
        children
      ) : (
        <PillLabel
          label={String(children)}
          short={short}
          chromeRem={chromeRem[size ?? 'md']}
        />
      )}
    </span>
  );
}

export { typePillVariants };
